// Contenido de marketing de los planes (onboarding y welcome comparten el
// mismo texto para no repetirlo dos veces): color de marca por tier,
// entitlements traducidos a lenguaje humano y highlights/limitaciones que no
// vienen del backend porque son una decisión de negocio fija, igual que en
// la tabla de planes.vue del landing (repos/landing/app/pages/planes.vue).

// Codes reales del backend: free/starter/pro/premium. El plan free no tiene
// entrada: se queda con el borde punteado neutro definido directo en el
// template. Reusa el mismo par borde/texto que ya usa business-type.vue para
// diferenciar sus 4 opciones.
export const TIER_ACCENT_CLASSES = {
    starter: { border: "border-brand-red", text: "text-brand-red" },
    pro: { border: "border-brand-blue", text: "text-brand-blue" },
    premium: { border: "border-brand-purple", text: "text-brand-purple" }
};

// Entitlements que se muestran en la tarjeta, en este orden.
export const FEATURE_LABELS = [
    { code: "products", emoji: "📦", one: "producto", many: "productos" },
    { code: "images", emoji: "🖼️", one: "imagen", many: "imágenes" },
    // Tope por periodo del plan: el sufijo ("al mes", "por trimestre",
    // "al año") sale de `plan.period`, ver ORDERS_PERIOD_SUFFIXES.
    {
        code: "ordersPerPeriod",
        emoji: "🛒",
        one: "pedido",
        many: "pedidos",
        periodic: true
    },
    { code: "users", emoji: "👤", one: "usuario", many: "usuarios" },
    {
        code: "blogs",
        emoji: "📝",
        one: "página personalizada",
        many: "páginas personalizadas"
    }
];

const ORDERS_PERIOD_SUFFIXES = {
    monthly: "al mes",
    quarterly: "por trimestre",
    yearly: "al año"
};

// Free: hasta 3 productos, sin categorias y con marca de agua.
export const FREE_PLAN_LIMITATIONS = [
    "🚫 Sin categorías",
    "💧 Con marca de agua"
];

// "Recibir órdenes por el catálogo" en la tabla comparativa del landing:
// distinto del entitlement `ordersPerPeriod` (que es venta/pedido interno
// registrado a mano, disponible desde Starter). Esto es el checkout propio
// del catálogo público — el cliente arma y envía el pedido sin salir del
// catálogo — solo Pro y Premium lo tienen.
export const PLAN_CATALOG_CHECKOUT = {
    free: false,
    starter: false,
    pro: true,
    premium: true
};

// Highlights cualitativos por tier — resumen de los mismos beneficios que ya
// destaca la tabla comparativa de planes.vue en el landing. Se muestran
// antes que las cantidades para vender el plan, no solo listar cupos.
export const PLAN_HIGHLIGHTS = {
    free: [
        "🔗 Perfil de enlaces para siempre",
        "🎨 Tema y colores personalizados"
    ],
    starter: [
        "✨ Catálogo sin marca de agua",
        "🧱 Editor de bloques",
        "📄 Cotizaciones en PDF"
    ],
    pro: [
        "🔗 URL personalizada",
        "🛍️ Recibe pedidos desde tu catálogo",
        "💳 Métodos de pago integrados",
        "📊 Inventario"
    ],
    premium: [
        "🌐 Dominio propio",
        "📊 Inventario y precios por sucursal",
        "🏬 Hasta 3 sucursales"
    ]
};

// El bullet visible en el collapse de welcome.vue es la accion generica, no
// el numero crudo — el numero y el "para que sirve" van en el detail de abajo
// (FEATURE_DETAILS). Las tarjetas de selección (buildPlanFeatures) siguen
// mostrando la cantidad directo, ahi si importa para comparar planes.
const FEATURE_ACTION_LABELS = {
    products: "📦 Registrar productos",
    images: "🖼️ Subir imágenes",
    ordersPerPeriod: "🛒 Registrar pedidos y ventas",
    users: "👤 Agregar usuarios",
    blogs: "📝 Publicar páginas personalizadas"
};

// Explica cuanto y para que sirve cada accion — solo para el collapse
// expandible de la pantalla final (welcome.vue).
const FEATURE_DETAILS = {
    products: {
        free: "Hasta 3 productos puedes registrar y publicar para empezar a mostrar lo que vendes.",
        starter:
            "Hasta 24 productos puedes registrar y publicar para que tus clientes vean lo que ofreces.",
        pro: "Hasta 120 productos puedes registrar y publicar para que tus clientes vean lo que ofreces.",
        premium:
            "Productos ilimitados: registra y publica todo tu catálogo sin límite."
    },
    images: {
        free: "Hasta 10 imágenes puedes subir para tu perfil, tus enlaces y tus productos.",
        starter:
            "Hasta 100 imágenes puedes subir para mostrar tus productos y tu catálogo.",
        pro: "Hasta 500 imágenes puedes subir — suficiente para varias fotos por producto.",
        premium:
            "Hasta 2.000 imágenes puedes subir, para catálogos grandes con múltiples fotos por referencia."
    },
    ordersPerPeriod: {
        free: "El plan Free no recibe pedidos desde el catálogo.",
        starter:
            "Hasta 50 pedidos al mes puedes registrar, de forma interna o desde el catálogo.",
        pro: "Hasta 500 pedidos al mes puedes registrar — pensado para un negocio con ventas activas.",
        premium:
            "Hasta 2.000 pedidos al mes puedes registrar, para operaciones de mayor volumen."
    },
    users: {
        free: "1 usuario puede administrar el catálogo: solo tú.",
        starter: "1 usuario puede administrar el catálogo: solo tú.",
        pro: "Hasta 3 usuarios puedes agregar para que tu equipo administre pedidos y productos.",
        premium:
            "Hasta 5 usuarios puedes agregar — ideal si varias personas o sedes necesitan acceso al panel."
    },
    blogs: {
        free: "El plan Free no incluye páginas personalizadas.",
        starter:
            "Hasta 5 páginas personalizadas puedes publicar para contarle a tus clientes sobre tu negocio: contacto, quiénes somos o un blog.",
        pro: "Hasta 20 páginas personalizadas puedes publicar.",
        premium: "Hasta 100 páginas personalizadas puedes publicar."
    }
};

// Version "marketera" de PLAN_HIGHLIGHTS, en frase completa — para el bloque
// de welcome.vue que no necesita collapse (no hay un numero/limite que
// explicar aparte, la frase ya lo dice todo). `key` identifica la frase para
// poder reemplazarla en un caso puntual (ver `customUrl` + `isTrial` abajo)
// sin depender de comparar el texto completo.
const HIGHLIGHT_SENTENCES = {
    free: [
        {
            key: "linksForever",
            text: "🔗 Tu página con tus redes y enlaces queda disponible para siempre, sin costo, aunque nunca cambies de plan."
        },
        {
            key: "products",
            text: "📦 Muestra hasta 3 productos con foto y precio para que tus clientes te escriban por WhatsApp."
        },
        {
            key: "theme",
            text: "🎨 Elige los colores de tu marca para que tu perfil se vea como tuyo, no como una plantilla genérica."
        }
    ],
    starter: [
        {
            key: "noWatermark",
            text: "✨ Tu catálogo se ve profesional, sin ningún aviso de tucatalogo.co."
        },
        {
            key: "blockEditor",
            text: "🧱 Arma tu página con bloques (banners, testimonios, enlaces) en el orden que quieras, sin necesidad de un diseñador."
        },
        {
            key: "quotesPdf",
            text: "📄 Genera cotizaciones formales para tus clientes directo desde el panel, sin salir a otra herramienta."
        }
    ],
    pro: [
        {
            key: "customUrl",
            text: "🔗 Tu catálogo vive en una URL propia en vez de un link genérico, dando más confianza a tus clientes."
        },
        {
            key: "catalogCheckout",
            text: "🛍️ Tus clientes arman su pedido y lo envían directo desde el catálogo, sin depender solo de WhatsApp."
        },
        {
            key: "paymentMethods",
            text: "💳 Activa pasarelas de pago para que te paguen en línea, sin coordinar transferencias manuales."
        },
        {
            key: "inventory",
            text: "📊 Controla el stock de cada producto, con descuento automático en cada venta."
        }
    ],
    premium: [
        {
            key: "ownDomain",
            text: "🌐 Usa tu propio dominio (ej. www.tunegocio.com) en vez de un subdominio de tucatalogo.co."
        },
        {
            key: "stores",
            text: "🏬 Hasta 3 sucursales, cada una con su inventario y sus precios."
        }
    ]
};

const TRIAL_CUSTOM_URL_SENTENCE =
    "🔗 Durante tu prueba, tu catálogo usa la URL generada al crear tu cuenta. Se activa una URL personalizada cuando actives el pago.";

// Version en frase completa de FREE_PLAN_LIMITATIONS, mismo criterio de
// arriba: sin numero que explicar, no necesita collapse.
const LIMITATION_SENTENCES = {
    free: [
        "🚫 Sin categorías, bloques ni pedidos desde el catálogo. Con Starter llegas a 24 productos y armas tu página con bloques.",
        "💧 Tu página incluye un aviso de tucatalogo.co, que se quita a partir del plan Starter."
    ]
};

// Bullets planos (sin collapse) para welcome.vue: highlights + limitaciones
// en frase completa, ya con el caso puntual de la URL personalizada en
// prueba resuelto.
export function buildPlanMarketingLines(plan, { isTrial = false } = {}) {
    const code = plan?.code;
    const highlights = (HIGHLIGHT_SENTENCES[code] || []).map((item) =>
        isTrial && code === "pro" && item.key === "customUrl"
            ? TRIAL_CUSTOM_URL_SENTENCE
            : item.text
    );
    return [...highlights, ...(LIMITATION_SENTENCES[code] || [])];
}

// Entitlements activos de un plan (quantity !== 0), con el label completo —
// filtro compartido por buildPlanFeatures y buildPlanLimitLines, cada una
// arma el texto visible a su manera.
function activeEntitlements(plan) {
    const entitlements = plan?.entitlements || [];
    return FEATURE_LABELS.reduce((lines, label) => {
        const entitlement = entitlements.find(
            (item) => item.resourceCode === label.code
        );
        if (!entitlement || entitlement.quantity === 0) {
            return lines;
        }
        lines.push({ label, entitlement });
        return lines;
    }, []);
}

// Arma la lista de features de una tarjeta: highlights cualitativos primero,
// despues las cantidades reales de `plan.entitlements`.
export function buildPlanFeatures(plan) {
    const highlights = PLAN_HIGHLIGHTS[plan?.code] || [];
    const quantities = activeEntitlements(plan).map(
        ({ entitlement, label }) => {
            if (entitlement.quantity === null) {
                const noun =
                    label.many.charAt(0).toUpperCase() + label.many.slice(1);
                return `${label.emoji} ${noun} ilimitados`;
            }
            const noun = entitlement.quantity === 1 ? label.one : label.many;
            if (label.periodic) {
                const suffix =
                    ORDERS_PERIOD_SUFFIXES[plan.period] ||
                    ORDERS_PERIOD_SUFFIXES.monthly;
                return `${label.emoji} ${entitlement.quantity.toLocaleString(
                    "es-CO"
                )} ${noun} ${suffix}`;
            }
            return `${label.emoji} ${entitlement.quantity} ${noun}`;
        }
    );
    return [...highlights, ...quantities];
}

// Unicas lineas con collapse en welcome.vue: son las que sí tienen un cupo
// que explicar (bullet = acción genérica, detail = cuánto y para qué). Todo
// lo demás (highlights, limitaciones) ya va en frase completa sin collapse,
// ver buildPlanMarketingLines.
export function buildPlanLimitLines(plan) {
    const code = plan?.code;
    return activeEntitlements(plan).map(({ label }) => ({
        text: FEATURE_ACTION_LABELS[label.code],
        detail: FEATURE_DETAILS[label.code]?.[code] || ""
    }));
}
